import type { ReactElement } from 'react';

import { Text, useTheme, Box, Grid } from '@simplywallst/ui-core';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';

import { pluralise } from '@/utilities/pluralise';

import { DeleteButton, DropdownItem, DropdownItemIcon } from './styled';

type Option = {
  id: string;
  name: string;
  totalItems: number;
};

type SecondaryOption = {
  label: string;
  value: string;
  icon: ReactElement;
};

type Props<T> = {
  option: T;
  isSelected: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onTouchEnd?: (yPos: number, value: string, label: string) => void;
  onSelect?: (value: string, label: string) => void;
  'data-cy-id'?: string;
};

export enum WatchlistSelectOptions {
  CREATE_LIST = 'create-list',
  SELECT_LIST = 'select-list',
  DELETE_LIST = 'delete-list',
}

export const WatchlistDropdownItem = ({
  option,
  onBlur,
  onFocus,
  onSelect,
  isSelected,
  onTouchEnd,
  'data-cy-id': dataCyId,
}: Props<Option>) => {
  const theme = useTheme();
  return (
    <Box position="relative" data-cy-id={dataCyId}>
      <DropdownItem
        isSelected={isSelected}
        tabIndex={-1}
        onBlur={onBlur}
        onFocus={onFocus}
        onClick={() =>
          onSelect?.(WatchlistSelectOptions.SELECT_LIST, option.id)
        }
        onTouchEnd={(e) => {
          e.preventDefault();
          onTouchEnd?.(
            e.changedTouches[0].pageY,
            WatchlistSelectOptions.SELECT_LIST,
            option.id
          );
        }}
      >
        <Grid templateColumns="36px auto" gap="8px" alignItems="start">
          <Grid
            alignItems="center"
            justifyContent="flex-end"
            minHeight="36px"
            minWidth="36px"
          >
            {isSelected && (
              <CheckSolid fill={theme.color.brand01} height="28" width="28" />
            )}
          </Grid>
          <Grid
            paddingTop="x1"
            paddingBottom="x1"
            paddingRight="x1"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Text typography="xSmall" color="spaceGray">
              {option.name}
            </Text>
            <Text typography="tiny" color="mediumGray">
              {option.totalItems}{' '}
              {pluralise(option.totalItems, 'company', 'companies')}
            </Text>
          </Grid>
        </Grid>
      </DropdownItem>
      <DeleteButton
        aria-label="Delete list"
        onBlur={onBlur}
        onFocus={onFocus}
        tabIndex={-1}
        onClick={() => {
          onSelect?.(WatchlistSelectOptions.DELETE_LIST, option.id);
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          onTouchEnd?.(
            e.changedTouches[0].pageY,
            WatchlistSelectOptions.DELETE_LIST,
            option.id
          );
        }}
      >
        <CrossSolid />
      </DeleteButton>
    </Box>
  );
};

export const WatchlistDropdownSecondaryItem = ({
  option,
  onBlur,
  onFocus,
  onSelect,
  isSelected,
  onTouchEnd,
  'data-cy-id': dataCyId,
}: Props<SecondaryOption>) => (
  <DropdownItem
    data-cy-id={dataCyId}
    isSelected={isSelected}
    tabIndex={-1}
    onBlur={onBlur}
    onFocus={onFocus}
    onClick={() => onSelect?.(option.value, option.label)}
    onTouchEnd={(e) => {
      e.preventDefault();
      onTouchEnd?.(e.changedTouches[0].pageY, option.value, option.label);
    }}
  >
    <Grid templateColumns="36px auto" gap="8px" alignItems="start">
      <Grid
        alignItems="center"
        justifyContent="flex-end"
        minHeight="36px"
        minWidth="36px"
      >
        <DropdownItemIcon>{option.icon}</DropdownItemIcon>
      </Grid>
      <Grid
        paddingTop="x1"
        paddingBottom="x1"
        paddingRight="x1"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Text typography="xSmall" color="spaceGray" textAlign="start">
          {option.label}
        </Text>
      </Grid>
    </Grid>
  </DropdownItem>
);
